import React from 'react'
import EditText from 'blocks.simple/editText/editText'
import translate from 'core/translate'
import { cn } from 'ethcss'
import { Typography } from 'atoms/Typography'
import { SwitchButton } from 'atoms/SwitchButton'
import styles from './ScreenAndSleepMode.jcss'

type ScreenAndSleepModeSettings = {
    networkStandby?: boolean
    refreshScreenAfterWakeupMS?: number | string
    autoSleepTimeAppLevelApi?: boolean
    goSleepAfterMS?: number | string
    timeWakeUpEveryDay?: string
    screenRefreshTimeEveryDay?: string
}

type ScreenAndSleepModeProps = {
    settings: ScreenAndSleepModeSettings
    onChange: (value: any, field: string) => void
}

type SwitchSettingProps = {
    title: string
    hint: string
    checked: boolean
    onChange: () => void
}

type InputSettingProps = {
    title: string
    hint: string
    label?: string
    value?: number | string
    type?: 'text' | 'number'
    mask?: string
    min?: number
    onChange: (value: string) => void
}

const MILLISECONDS_IN_SECOND = 1000

const millisecondsToSeconds = (value?: number | string) => {
    if (value === undefined || value === null || value === '') {
        return ''
    }

    const milliseconds = Number(value)

    return Number.isNaN(milliseconds) ? '' : milliseconds / MILLISECONDS_IN_SECOND
}

const secondsToMilliseconds = (value: string) => {
    if (value === '') {
        return ''
    }

    const seconds = Number(value)

    return Number.isNaN(seconds) ? '' : seconds * MILLISECONDS_IN_SECOND
}

const SwitchSetting = ({ title, hint, checked, onChange }: SwitchSettingProps) => (
    <div className={styles.setting}>
        <div className={styles.switchWrapper}>
            <SwitchButton
                checked={checked}
                onChange={onChange}
                className={`test-settings-toggle_${checked ? 'enabled' : 'disabled'}`}
            />
            <div className={cn(styles.switchCaption)}>
                <Typography type="title">{title}</Typography>
            </div>
        </div>
        <div className={styles.hint}>{hint}</div>
    </div>
)

const InputSetting = ({ title, hint, label, value, type = 'text', mask, min, onChange }: InputSettingProps) => (
    <div className={styles.setting}>
        <Typography type="title">{title}</Typography>
        <div className={styles.hint}>{hint}</div>
        <div className={styles.field}>
            <EditText
                mod={'withBorder'}
                label={label}
                type={type}
                mask={mask}
                min={min}
                value={value === undefined || value === null ? '' : value}
                onChange={onChange}
            />
        </div>
    </div>
)

const ScreenAndSleepMode = ({ settings = {}, onChange }: ScreenAndSleepModeProps) => (
    <div className={styles.content}>
        <SwitchSetting
            title={translate('networkStandby')}
            hint={translate('networkStandbyHint')}
            checked={Boolean(settings.networkStandby)}
            onChange={() => onChange(!settings.networkStandby, 'networkStandby')}
        />
        <SwitchSetting
            title={translate('autoSleepAppLevel')}
            hint={translate('autoSleepAppLevelHint')}
            checked={Boolean(settings.autoSleepTimeAppLevelApi)}
            onChange={() => onChange(!settings.autoSleepTimeAppLevelApi, 'autoSleepTimeAppLevelApi')}
        />
        <InputSetting
            title={translate('refreshScreenAfterWakeup')}
            hint={translate('refreshScreenAfterWakeupHint')}
            label={translate('seconds')}
            type="number"
            min={240}
            value={millisecondsToSeconds(settings.refreshScreenAfterWakeupMS)}
            onChange={(value) => onChange(secondsToMilliseconds(value), 'refreshScreenAfterWakeupMS')}
        />
        <InputSetting
            title={translate('goSleepAfter')}
            hint={translate('goSleepAfterHint')}
            label={translate('seconds')}
            type="number"
            min={60}
            value={millisecondsToSeconds(settings.goSleepAfterMS)}
            onChange={(value) => onChange(secondsToMilliseconds(value), 'goSleepAfterMS')}
        />
        <InputSetting
            title={translate('timeWakeUpEveryDay')}
            hint={translate('timeWakeUpEveryDayHint')}
            mask="99:99:99"
            value={settings.timeWakeUpEveryDay}
            onChange={(value) => onChange(value, 'timeWakeUpEveryDay')}
        />
        <InputSetting
            title={translate('screenRefreshTimeEveryDay')}
            hint={translate('screenRefreshTimeEveryDayHint')}
            mask="99:99:99"
            value={settings.screenRefreshTimeEveryDay}
            onChange={(value) => onChange(value, 'screenRefreshTimeEveryDay')}
        />
    </div>
)

export default React.memo(ScreenAndSleepMode)
